import { useQuery } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { getActiveRoleRequestHeaders } from "@/lib/utils/api/active-role-header";
import { QueryKeys } from "@/lib/types";
import type { GetAppointmentTypeResponse } from "@/openapi/generated/models";
import { GetAppointmentTypeResponseFromJSON } from "@/openapi/generated/models";

interface IUseAppointmentTypesGetProps {
  enabled?: boolean;
}

export const useAppointmentTypesGet = ({
  enabled = true,
}: IUseAppointmentTypesGetProps = {}) => {
  const accessToken = useAccessToken();

  const { data, isError, isLoading, isSuccess, isPending } = useQuery({
    queryKey: [QueryKeys.AppointmentTypes],
    queryFn: async (): Promise<Array<GetAppointmentTypeResponse>> => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }

      const apiUrl = process.env.NEXT_PUBLIC_API_URL || "";
      const response = await fetch(`${apiUrl}/api/appointment/types`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...getActiveRoleRequestHeaders(),
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch appointment types: ${response.statusText}`);
      }

      const jsonData = await response.json();
      return jsonData.map((item: any) => GetAppointmentTypeResponseFromJSON(item));
    },
    enabled: enabled && !!accessToken,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 5 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  return {
    data,
    isError,
    isLoading,
    isSuccess,
    isPending,
  };
};
